import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ChevronDown, Gift, MessageCircle, Star } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { isNative } from "@/lib/native";
import { usePromo, PromoCountdown } from "@/lib/promo";
import { READING_BY_SLUG } from "@/lib/readingsContent";
import CelestialBackground from "@/components/CelestialBackground";
import { SignGrid, ModeToggle } from "@/components/SignGrid";
import { PublicNav } from "@/components/BottomNav";
import SiteMenu from "@/components/SiteMenu";
import PreviewSheet from "@/components/PreviewSheet";

const HERO = "https://images.unsplash.com/photo-1507400492013-162706c8c05e?w=900&auto=format&crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwzfHxuaWdodCUyMHNreSUyMHN0YXJzJTIwY29uc3RlbGxhdGlvbnxlbnwwfHx8fDE3OTEyMDcwOTN8MA&ixlib=rb-4.1.0&q=70";


function AnswerOfWeek() {
  const [a, setA] = useState(null);
  useEffect(() => {
    api.get("/public/answer-of-week").then(({ data }) => setA(data.answer)).catch(() => {});
  }, []);
  if (!a) return null;
  return (
    <Link to={`/a/${a.token}`} data-testid="landing-answer-of-week"
      className="block mt-8 bg-[#0B0F19]/80 border border-[#D4AF37]/30 hover:border-[#D4AF37]/60 rounded-3xl p-6 transition-colors">
      <p className="flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] gold-text mb-3">
        <Star size={13} fill="#D4AF37" /> Answer of the week
      </p>
      <p className="text-sm text-[#A7B0C0] mb-2">{a.name || "A member"}{a.sun_sign && ` (${a.sun_sign})`} asked:</p>
      <p data-testid="aotw-question" className="text-xl font-light italic text-starlight mb-3">&ldquo;{a.question}&rdquo;</p>
      <p data-testid="aotw-teaser" className="text-base text-[#D6D0C5] leading-relaxed line-clamp-3 lg:line-clamp-6">{a.teaser}</p>
      <p className="text-sm gold-text mt-3 lg:mt-auto lg:pt-3">Read Astra Interactive's answer →</p>
    </Link>
  );
}

function InteractiveCard() {
  return (
    <div data-testid="landing-interactive-card" className="mt-8 bg-[#0F1523] border border-[#D4AF37]/35 rounded-3xl p-6">
      <p className="flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] gold-text mb-3">
        <MessageCircle size={14} /> Astra Interactive
      </p>
      <h3 className="text-2xl font-light leading-snug mb-3">
        Does your current daily horoscope leave you with unanswered questions?
      </h3>
      <p className="text-base text-[#D6D0C5] leading-relaxed mb-3">
        With Astra Interactive you can ask the stars questions. Astra Interactive will provide answers, and she will
        remember the conversation for your next interaction.
      </p>
      <p className="text-base text-[#D6D0C5] leading-relaxed mb-5">
        Put it to the test: try Astra Interactive now and see what guidance you receive.
      </p>
      <Link
        to="/try"
        data-testid="btn-card-interactive"
        className="block text-center w-full bg-[#D4AF37] hover:bg-[#e6c04e] text-[#04060B] font-medium text-lg py-3.5 rounded-full transition-colors"
      >
        Try Astra Interactive free
      </Link>
    </div>
  );
}

function DesktopOrbit() {
  return (
    <div data-testid="desktop-hero-orbit" className="hidden lg:flex flex-col items-center justify-center relative">
      <div className="relative w-[380px] h-[380px] flex items-center justify-center">
        <img src="/astra-icon-transparent.png" alt="Astra" className="relative w-56 h-56" />
      </div>
      <p className="mt-6 text-sm tracking-[0.3em] uppercase text-[#A7B0C0]">Sun · Moon · Mercury · Venus · Mars</p>
      <p className="mt-1 text-sm text-[#D6D0C5]">Read from today's real sky</p>
    </div>
  );
}

function OfferBanner() {
  const promo = usePromo();
  const r = promo && READING_BY_SLUG[promo.slug];
  if (isNative || !r) return null;
  return (
    <Link to={`/readings/${promo.slug}`} data-testid="home-offer-banner"
      className="group mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 max-w-xl bg-[#1a1230]/80 backdrop-blur-md border border-[#D4AF37]/60 hover:border-[#D4AF37] rounded-2xl px-5 py-4 transition-colors">
      <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] bg-[#D4AF37] text-[#04060B] px-2.5 py-1 rounded-full"><Gift size={11} /> Special offer</span>
      <span className="text-base text-starlight">Your <span className="gold-text">{r.title}</span>, FREE with Premium</span>
      <PromoCountdown endsAt={promo.ends_at} />
      <ArrowRight size={16} className="gold-text ml-auto group-hover:translate-x-1 transition-transform" />
    </Link>
  );
}

function Hero({ wide, onChart }) {
  const scroll = () => document.getElementById("signs")?.scrollIntoView({ behavior: "smooth" });
  return (
    <div className="relative min-h-[70vh] lg:min-h-[86vh] pb-10 overflow-hidden" data-testid="landing-hero">
      <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60"
        style={{ maskImage: "linear-gradient(to bottom, #000 45%, transparent 100%)", WebkitMaskImage: "linear-gradient(to bottom, #000 45%, transparent 100%)" }} />
      <div className="absolute inset-0 bg-gradient-to-b from-[#04060B]/20 via-[#04060B]/50 to-transparent" />
      <header className="relative safe-top box-content h-16 lg:h-20 px-5 lg:px-10 lg:max-w-6xl lg:mx-auto flex items-center gap-1">
        <SiteMenu />
        <img src="/astra-mark-128.png" alt="" data-testid="hero-logo-icon" aria-hidden="true" className="w-9 h-9 -mr-0.5" />
        <span className="font-display text-lg tracking-[0.3em]">ASTRA</span>
        {wide && (
          <nav data-testid="desktop-nav" className="hidden lg:flex items-center gap-8 ml-14 text-base text-[#D6D0C5]">
            <button data-testid="desktop-nav-signs" onClick={scroll} className="hover:text-starlight transition-colors">Today's horoscopes</button>
            <button onClick={onChart} data-testid="desktop-nav-chart" className="hover:text-starlight transition-colors">Chart</button>
            <Link to="/readings" data-testid="desktop-nav-readings" className="hover:text-starlight transition-colors">Readings</Link>
            <Link to="/try" data-testid="desktop-nav-interactive" className="hover:text-starlight transition-colors">Astra Interactive</Link>
          </nav>
        )}
        <button onClick={onChart} data-testid="link-landing-chart" className={`ml-auto text-sm text-[#D6D0C5] hover:text-starlight py-2.5 px-2 ${wide ? "lg:hidden" : ""}`}>Chart</button>
        <Link to="/readings" data-testid="link-landing-readings" className={`text-sm text-[#D6D0C5] hover:text-starlight py-2.5 px-2 ${wide ? "lg:hidden" : ""}`}>Readings</Link>
        <Link to="/join?mode=login" data-testid="link-landing-signin" className={`text-sm lg:text-base gold-text py-2.5 px-1 lg:px-3 ${wide ? "lg:ml-auto" : ""}`}>Sign in</Link>
        {wide && (
          <Link to="/join" data-testid="desktop-nav-join"
            className="hidden lg:inline-block ml-3 bg-[#D4AF37] hover:bg-[#e6c04e] text-[#04060B] font-medium px-6 py-2.5 rounded-full transition-colors">
            Create free account
          </Link>
        )}
      </header>
      <div className={`relative px-6 pt-[10vh] fade-up ${wide ? "lg:max-w-6xl lg:mx-auto lg:px-10 lg:pt-[8vh] lg:grid lg:grid-cols-[1.15fr_0.85fr] lg:gap-16 lg:items-center" : ""}`}>
        <div>
          <p className="text-[11px] uppercase tracking-[0.35em] gold-text mb-3">Free daily horoscope</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light leading-tight mb-4">
            Astra: It Is Written In The Stars
          </h1>
          <p className="text-base lg:text-lg text-[#D6D0C5] mb-8 max-w-sm lg:max-w-lg">
            Every reading is based on where the planets actually are today. Pick your sign and read, no sign-up needed.
          </p>
          <div className="lg:flex lg:flex-wrap lg:items-center lg:gap-4">
            <button
              data-testid="btn-read-horoscopes"
              onClick={scroll}
              className="flex items-center gap-2 bg-[#D4AF37] hover:bg-[#e6c04e] text-[#04060B] font-medium text-lg px-7 py-3.5 rounded-full transition-colors"
            >
              Read today's horoscopes <ChevronDown size={18} />
            </button>
            <Link
              to="/try"
              data-testid="btn-hero-interactive"
              className="mt-4 lg:mt-0 inline-flex items-center gap-2 border border-[#D4AF37]/50 hover:bg-[#D4AF37]/10 text-[#E8DCC4] text-base px-6 py-3 rounded-full transition-colors"
            >
              <MessageCircle size={16} className="gold-text" />
              <span>Ask the stars with <span className="gold-text">Astra Interactive</span> – try it now for free</span>
            </Link>
          </div>
          <OfferBanner />
        </div>
        {wide && <DesktopOrbit />}
      </div>
    </div>
  );
}

export default function Horoscopes() {
  const navigate = useNavigate();
  const [locked, setLocked] = useState(null);
  const [mode, setMode] = useState("daily");
  const wide = !isNative;

  return (
    <div className={`app-shell relative text-starlight ${wide ? "wide-desktop" : ""}`}>
      <CelestialBackground />
      <div className={`relative z-10 pb-28 ${wide ? "lg:pb-12" : ""}`} data-testid="horoscopes-page">
        <Hero wide={wide} onChart={() => setLocked("Chart")} />
        <section id="signs" className={`px-5 pt-6 scroll-mt-4 ${wide ? "lg:max-w-6xl lg:mx-auto lg:px-10 lg:pt-10" : ""}`}>
          <div className={wide ? "lg:flex lg:items-end lg:justify-between lg:gap-8 lg:mb-6" : ""}>
            <div>
              <p className="text-[11px] uppercase tracking-[0.3em] gold-text">Today</p>
              <h2 className="text-3xl lg:text-4xl font-light mt-1 mb-4">Choose your star sign</h2>
            </div>
            <div className={wide ? "lg:w-[420px]" : ""}>
              <ModeToggle mode={mode} setMode={setMode} />
            </div>
          </div>
          <SignGrid onPick={(s) => navigate(`/s/${s.toLowerCase()}/${mode === "weekly" ? "weekly" : mode === "monthly" ? "monthly" : "daily"}`)} className={wide ? "lg:grid-cols-6 lg:gap-4" : ""} />
          <Link
            to="/join"
            data-testid="btn-horoscopes-cta"
            className={`block text-center w-full mt-6 bg-[#0F1523] border border-[#D4AF37]/40 hover:bg-[#D4AF37]/10 gold-text text-lg py-3.5 rounded-full transition-colors ${wide ? "lg:w-auto lg:inline-block lg:px-12 lg:mt-8" : ""}`}
          >
            Get a reading for your exact birth chart
          </Link>
          <div className={wide ? "lg:grid lg:grid-cols-2 lg:gap-6 lg:items-stretch lg:mt-4 lg:[&>*]:h-full lg:[&>*]:flex lg:[&>*]:flex-col" : ""}>
            <AnswerOfWeek />
            <InteractiveCard />
          </div>
        </section>
      </div>
      <PublicNav onLocked={setLocked} className={wide ? "lg:hidden" : ""} />
      <PreviewSheet label={locked} onClose={() => setLocked(null)} />
    </div>
  );
}
